import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Chip, ChipGroup } from '@calendly/ui/components/chip';
import { DatePicker } from '@calendly/ui/components/date-picker';
import { Icon } from '@calendly/ui/components/icon';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { Popover, PopoverContent, PopoverTrigger } from '@calendly/ui/components/popover';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';
import { MEETINGS_PRESET_IDS } from 'constants/meetings';
import { useMeetingsPresetTabsScrollBand } from './hooks/use_meetings_preset_tabs_scroll_band';
import { useMeetingsViewDate } from './hooks/use_meetings_view_date';
import { useUpcomingPreset } from './hooks/use_upcoming_preset';
const PRESET_CHIP_ITEMS = [{
  id: MEETINGS_PRESET_IDS.UPCOMING,
  labelKey: 'publisher.meetings.filter_modal.dates.upcoming',
  dataLabel: 'meetings_page_upcoming_filter'
}, {
  id: MEETINGS_PRESET_IDS.TODAY,
  labelKey: 'publisher.meetings.filter_modal.dates.today',
  dataLabel: 'meetings_page_today_filter'
}, {
  id: MEETINGS_PRESET_IDS.THIS_WEEK,
  labelKey: 'publisher.meetings.filter_modal.dates.this_week',
  dataLabel: 'meetings_page_this_week_filter'
}, {
  id: MEETINGS_PRESET_IDS.LAST_WEEK,
  labelKey: 'publisher.meetings.filter_modal.dates.last_week',
  dataLabel: 'meetings_page_last_week_filter'
}];

/** Figma 3250:194439 — utility bar preset row: flex, 16px gaps, Day Header → divider (25px) → chips. */
const rowCss = "r9yu25i";
const rowIsPhoneCss = "rjp9pz9";
const leadingControlsCss = "l1qnkflf";
const leadingControlsIsPhoneCss = "lhytpis";

/** Figma Day Header: padding 8px 0, date + chevron. */
const dayHeaderCss = "d1cw4gq9";
const chipsContainerCss = "c14h7nvk";
const chipsContainerIsPhoneCss = "cxbx3yn";
const dateTriggerIsPhoneCss = "d1jub4pc";
const chipGroupIsPhoneCss = "c1judpff";
const dateTriggerCss = "d8dhfbu";
const dateTriggerContentCss = "d1cglagu";
const dateLabelCss = "d1ckaei3";

/* Figma: date picker label steps down to title-small on phone widths. */
const dateLabelIsPhoneCss = "dlblm5i";
const dateChevronCss = "d9w2ij4";

/** Figma divider slot: 25px between Day Header and chip group. */
const dividerCss = "dlzm0pd";
const mobilePresetCountDividerCss = "mhorzyj";
const meetingCountCss = "m1vmx4z6";
const meetingCountIsPhoneCss = "m1mrkhy6";
const datePickerPopoverCss = "d16lc968";
const datePickerFooterCss = "d8n3bko";
const datePickerCalendarCss = "d1dsyvkv";

/**
 * Preset header row shown on the meetings page when `sa_default_preset_toggle` is on.
 *
 * Left: a clickable date that opens a calendar popover. Picking a date re-centers the feed on
 * that date (past + future around it) via `useMeetingsViewDate`. The label reflects the open
 * right-rail meeting's date when one is open, otherwise scroll position, otherwise the feed pivot.
 *
 * Right: mutually exclusive date preset chips. See `useUpcomingPreset`.
 */
export const MeetingsPresetTabs = ({
  displayedMeetingCount = 0
}) => {
  const {
    activePreset,
    presetFlagOn,
    togglePreset
  } = useUpcomingPreset();
  const presetTabsBandRef = useMeetingsPresetTabsScrollBand(presetFlagOn);
  const isPhone = useDeviceType('isPhone');
  const {
    displayDate,
    today,
    timezone,
    selectDate
  } = useMeetingsViewDate();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const dateLabel = useMemo(() => {
    const parsed = moment(displayDate, 'YYYY-MM-DD');
    return (timezone ? parsed.tz(timezone, true) : parsed).format('ddd MMM D, YYYY');
  }, [displayDate, timezone]);
  const pickerDate = displayDate;
  const handleDateChange = useCallback(date => {
    selectDate(date);
    setIsCalendarOpen(false);
  }, [selectDate]);
  const handleTodayClick = useCallback(() => {
    selectDate(today);
    setIsCalendarOpen(false);
  }, [selectDate, today]);
  const items = useMemo(() => PRESET_CHIP_ITEMS.map(item => ({
    id: item.id,
    textValue: I18n.t(item.labelKey),
    dataLabel: item.dataLabel
  })), []);
  if (!presetFlagOn) return null;
  const handleSelectionChange = keys => {
    const selected = [...keys];
    if (selected.length === 0) {
      if (activePreset) togglePreset(activePreset);
      return;
    }
    const nextPresetId = selected.find(id => id !== activePreset) ?? selected[selected.length - 1];
    togglePreset(nextPresetId);
  };
  return <div ref={presetTabsBandRef} className={classNames(rowCss, {
    [rowIsPhoneCss]: isPhone
  })} data-mobile-layout={isPhone ? 'true' : undefined} data-testid="meetings-preset-tabs" data-column-layout="flex">
      <div className={classNames(leadingControlsCss, {
      [leadingControlsIsPhoneCss]: isPhone
    })}>
        <div className={dayHeaderCss}>
          <Popover isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)}>
            <PopoverTrigger>
              <Button decoration="ghost" size="small" responsive={false} className={classNames(dateTriggerCss, {
              [dateTriggerIsPhoneCss]: isPhone
            })} aria-expanded={isCalendarOpen} aria-haspopup="dialog" data-testid="meetings-preset-date-trigger" onClick={() => setIsCalendarOpen(open => !open)}>
                <span className={dateTriggerContentCss}>
                  <span className={classNames(dateLabelCss, {
                  [dateLabelIsPhoneCss]: isPhone
                })} data-testid="meetings-preset-date-label">
                    {dateLabel}
                  </span>
                  <span className={dateChevronCss} aria-hidden>
                    <Icon name={isCalendarOpen ? 'angle-up' : 'angle-down'} />
                  </span>
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className={datePickerPopoverCss} placement="bottom start">
              <div className={datePickerCalendarCss} data-testid="meetings-preset-date-picker">
                <DatePicker date={pickerDate} timezone={timezone} pastSelection onChange={handleDateChange} />
              </div>
              <div className={datePickerFooterCss}>
                <MinimalButton data-testid="meetings-preset-date-today" onClick={handleTodayClick}>
                  {I18n.t('publisher.meetings.filter_modal.dates.today')}
                </MinimalButton>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {!isPhone && <span className={dividerCss} aria-hidden="true" data-testid="meetings-preset-divider" />}
        <div className={classNames(chipsContainerCss, {
        [chipsContainerIsPhoneCss]: isPhone
      })} data-testid="meetings-preset-columns">
          <div className={classNames({
          [chipGroupIsPhoneCss]: isPhone
        })}>
            <ChipGroup aria-label={I18n.t('publisher.meetings.filter_modal.dates.label')} dependencies={['selectable']} items={items} onSelectionChange={handleSelectionChange} selectedKeys={activePreset ? [activePreset] : []} selectionMode="multiple">
              {item => <Chip key={item.id} aria-label={item.textValue} data-label={item.dataLabel} id={item.id} size="small" variant="selectable">
                  {item.textValue}
                </Chip>}
            </ChipGroup>
          </div>
        </div>
      </div>
      {isPhone && <span className={mobilePresetCountDividerCss} aria-hidden="true" data-testid="meetings-preset-count-divider" />}
      <BodyText as="span" color="muted" className={classNames(meetingCountCss, {
      [meetingCountIsPhoneCss]: isPhone
    })} data-testid="meetings-displayed-count">
        {I18n.t('publisher.meetings.preset_tabs.displaying_meetings', {
        count: displayedMeetingCount
      })}
      </BodyText>
    </div>;
};

require("./meetings_preset_tabs.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meetings_preset_tabs.js");